import { format, isSameYear } from 'date-fns'

/**
 * Returns a zero date.
 */
export function unixEpoch() {
  return new Date(0)
}

/**
 * Parses a string into a date if it's a string.
 * If `undefined, returns `undefined`. Intended for use in `Model.parse`.
 * If `null`, returns `null`.
 * If an invalid date, returns `undefined`.
 * @param value
 */
export function maybeParseDate<T extends string | null | undefined>(
  value: T,
): MaybeParsedDate<T> {
  // For some reason, we have to force these conversions.
  // The caller signature is correct though.
  if (value === null) {
    return null!
  }
  if (value === '' || value === undefined) {
    return undefined!
  }
  const result = new Date(value!)
  if (Number.isNaN(result.getTime())) {
    return undefined!
  }
  return result as MaybeParsedDate<T>
}

/**
 * Parses a date string, or returns {@link fallback} (the Unix epoch by
 * default) when it is missing or invalid.
 *
 * @param value
 * @param fallback
 */
export function safeParseDate(
  value: string | null | undefined,
  fallback?: Date,
): Date {
  return maybeParseDate(value) ?? fallback ?? unixEpoch()
}

type MaybeParsedDate<T> = T extends null
  ? Date | undefined | null
  : Date | undefined

export function timeago(date: Date, now?: Date): string
export function timeago(
  date: Date,
  style: keyof UnitFormats,
  now?: Date,
): string
export function timeago(date: Date, options: TimeagoOptions): string
/**
 * Returns a "timeago" style string from a date (and an optional "now").
 *
 * @param date
 * @param style Can be `short` (2s) or `long` (2 seconds), or the options
 * @param now
 */
export function timeago(
  date: Date,
  style?: keyof UnitFormats | Date | TimeagoOptions,
  now = new Date(),
): string {
  const options: TimeagoOptions =
    style instanceof Date
      ? { now: style }
      : typeof style === 'string'
        ? { style, now }
        : { now, ...style }
  const current = options.now ?? now
  const diff = current.getTime() - date.getTime()
  if (options.cutoff !== undefined && diff >= options.cutoff) {
    return format(date, isSameYear(date, current) ? 'MMM d' : 'MMM d, yyyy')
  }

  return formatDuration(diff, options.style ?? 'short')
}

/**
 * Options for {@link timeago}.
 */
export interface TimeagoOptions {
  style?: keyof UnitFormats
  now?: Date
  /**
   * Age in milliseconds from which the date itself is shown instead, as
   * "Mar 4", with the year when it isn't the current one.
   */
  cutoff?: number
}

/**
 * Formats a duration.
 *
 * @param durationMs
 * @param style
 * @param nowText
 */
export function formatDuration(
  durationMs: number,
  style: keyof UnitFormats,
  nowText = 'now',
) {
  const seconds = durationMs / 1000
  const unitFormat = UnitFormats[style]
  return seconds < 5
    ? nowText
    : seconds < 60
      ? formatWithUnit(seconds, unitFormat.s)
      : seconds < 60 * 60
        ? formatWithUnit(seconds / 60, unitFormat.m)
        : seconds < 60 * 60 * 24
          ? formatWithUnit(seconds / (60 * 60), unitFormat.h)
          : seconds < 60 * 60 * 24 * 31
            ? formatWithUnit(seconds / (60 * 60 * 24), unitFormat.d)
            : seconds < 60 * 60 * 24 * 365
              ? formatWithUnit(seconds / (60 * 60 * 24 * 31), unitFormat.mo)
              : formatWithUnit(seconds / (60 * 60 * 24 * 365), unitFormat.y)
}

function formatWithUnit(value: number, unitPair: UnitFormatPair) {
  const floored = Math.floor(value)
  const suffix = floored === 1 ? unitPair[0] : unitPair[1]
  return `${floored}${suffix}`
}

interface UnitFormat {
  s: UnitFormatPair
  m: UnitFormatPair
  h: UnitFormatPair
  d: UnitFormatPair
  mo: UnitFormatPair
  y: UnitFormatPair
}

type UnitFormatPair = [one: string, other: string]

interface UnitFormats {
  short: UnitFormat
  long: UnitFormat
}

const UnitFormats: UnitFormats = {
  short: {
    s: ['s', 's'],
    m: ['m', 'm'],
    h: ['h', 'h'],
    d: ['d', 'd'],
    mo: ['mo', 'mo'],
    y: ['y', 'y'],
  },
  long: {
    s: [' second', ' seconds'],
    m: [' minute', ' minutes'],
    h: [' hour', ' hours'],
    d: [' day', ' days'],
    mo: [' month', ' months'],
    y: [' year', ' years'],
  },
}

/**
 * Given a number of seconds, converts it into "minutes and seconds" of the
 * form "Xm Ys".
 */
export function formattedTimeUntil(secondsRemaining: number) {
  // Note: `~~` is a double bitwise operation and it performs a fast
  // `Math.floor`.

  if (secondsRemaining < 0) {
    return '0m 0s'
  }

  const days = ~~(secondsRemaining / 86400)
  const hours = ~~((secondsRemaining % 86400) / 3600)
  const minutes = ~~((secondsRemaining % 3600) / 60)
  const seconds = ~~(secondsRemaining % 60)

  const daysString = days !== 0 ? `${days}d ` : ''
  const hoursString = hours !== 0 ? `${hours}h ` : ''

  return `${daysString}${hoursString}${minutes}m ${seconds}s`
}

/**
 * Common date formatters.
 */
export const dateFormatters = {
  dateLong: new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }),
  dateNumeric: new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }),
  dateNumericPaddedDay: new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: '2-digit',
  }),
  dateAmerican: new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }),
  dateWeekday: new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }),
  dateTimeDefault: new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }),
  dateTimeWithoutYear: new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }),
  dateTimeWithZone: new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    timeZoneName: 'short',
  }),
  timeDefault: new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }),
  /**
   * The long date and the time, separated by a comma. `dateTimeDefault`
   * separates them with "at".
   */
  dateLongWithTime: {
    format(date: Date) {
      return `${dateFormatters.dateLong.format(date)}, ${dateFormatters.timeDefault.format(date)}`
    },
  },
}

/**
 * Parses a date in the `MM/dd/yyyy` format into a {@link Date} with local time
 * at midnight.
 *
 * @param str
 * @param pivot If specified, and the parsed year is 2 digits, determines the
 * century based on whether the year value is less than or greater than the
 * pivot. Typically, `49` is used.
 */
export function parseAmericanDate(str: string, pivot?: number): Date | null {
  const [, monthStr, dayStr, yearStr] =
    /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/.exec(str.trim()) ?? []

  if (!monthStr || !dayStr || !yearStr) {
    return null
  }

  let year = Number(yearStr)
  if (year <= 99) {
    if (pivot === undefined) {
      return null
    }

    year = year <= pivot ? 2000 + year : 1900 + year
  }

  return new Date(year, Number(monthStr) - 1, Number(dayStr), 0, 0, 0, 0)
}
